import { useEffect, useState } from "react";
import { GraduationCap, Clock, MapPin, BarChart2, ShieldCheck, Users, ArrowRight } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { api } from "../../lib/api";
import { Section, VCOLOR, VERTICAL_KEYS } from "./Primitives";
import { SessionRequestModal } from "./SessionRequestModal";

const pick = (m, k, lang) => m[`${k}_${lang}`] || m[`${k}_fr`];

const Meta = ({ icon: Icon, label, value }) => (
  <div className="ac-meta"><Icon aria-hidden="true" /><span>{label}</span><b>{value}</b></div>
);

const ModuleCard = ({ m, onRequest }) => {
  const { t, lang } = useLang();
  const a = t.academy;
  return (
    <article className="panel ac-card panel-hover" style={{ "--c": VCOLOR[m.vertical] }} data-testid={`academy-module-${m.id}`}>
      <div className="ac-top">
        <span className="ac-sector"><i aria-hidden="true" />{t.verticals[m.vertical]}</span>
        <span className="ac-std" data-testid={`academy-module-standard-${m.id}`}><ShieldCheck aria-hidden="true" />{m.standard}</span>
      </div>
      <h3>{pick(m, "title", lang)}</h3>
      <p className="ac-desc">{pick(m, "desc", lang)}</p>
      <p className="ac-label">{a.objectives}</p>
      <ul className="ac-obj">{pick(m, "objectives", lang).map((o) => <li key={o}>{o}</li>)}</ul>
      <div className="ac-metas">
        <Meta icon={Clock} label={a.duration} value={pick(m, "duration", lang)} />
        <Meta icon={MapPin} label={a.format} value={a.formats[m.format]} />
        <Meta icon={BarChart2} label={a.level} value={a.levels[m.level]} />
        <Meta icon={Users} label={a.audience} value={pick(m, "audience", lang)} />
      </div>
      <button type="button" className="btn btn-primary btn-sm ac-cta" onClick={() => onRequest(m)} data-testid={`academy-request-btn-${m.id}`}>
        {a.request} <ArrowRight className="arrow" aria-hidden="true" />
      </button>
    </article>
  );
};

export const Academy = () => {
  const { t } = useLang();
  const a = t.academy;
  const [modules, setModules] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  useEffect(() => { api.get("/academy/modules").then((r) => setModules(r.data)).catch(() => setModules([])); }, []);

  const shown = (modules || []).filter((m) => filter === "all" || m.vertical === filter);
  return (
    <Section id="academie" eyebrow={a.eyebrow} title={a.title} desc={a.desc} testid="academy-section">
      <div className="tabs" role="group" aria-label={a.filterLabel} data-testid="academy-filter">
        {["all", ...VERTICAL_KEYS].map((k) => (
          <button key={k} type="button" className="tab-btn" aria-pressed={filter === k} aria-selected={filter === k} onClick={() => setFilter(k)} data-testid={`academy-filter-${k}`}>
            {k === "all" ? <GraduationCap aria-hidden="true" /> : <i className="ac-dot" style={{ "--c": VCOLOR[k] }} aria-hidden="true" />}
            {k === "all" ? a.all : t.verticals[k]}
          </button>
        ))}
      </div>
      {modules && !shown.length && <p className="empty" data-testid="academy-empty">{a.none}</p>}
      <div className="ac-grid" data-testid="academy-grid">
        {shown.map((m) => <ModuleCard key={m.id} m={m} onRequest={setSelected} />)}
      </div>
      <p className="footnote">{a.quoteNote}</p>
      {selected && <SessionRequestModal module={selected} onClose={() => setSelected(null)} />}
    </Section>
  );
};
